import {
  elementalsSetRandomElements,
  elementalsSpendElement,
  markConstructionPlanApplied,
  setHexToken,
  setSpecialBuilding
} from "../../../../redux/actions";
import {
  selectCurrentPlayer,
  selectElementalsConstructionTokens,
  selectRound
} from "../../../../redux/reducers/sharedStateReducer/selectors";
import {
  selectConstructionPlanAppliedTurnKey,
  selectConstructionPlanEntries,
  selectConstructionPlanIsAutoCompleteTurn
} from "../../../../redux/reducers/privateStateReducer/selectors";
import {
  CONSTRUCTION_PLAN_CONTEXTS,
  getConstructionPlanContext,
  getConstructionPlanPlayer,
  getConstructionPlanTurnKey
} from "./getConstructionPlanContext";
import {
  CONSTRUCTION_PLACEMENT_KINDS,
  resolveConstructionPlacement,
  TEXT_CELL_NOT_AVAILABLE_FOR_CONSTRUCTION
} from "../resolveConstructionPlacement";
import {highlightInvalidPendingBuildings} from "../pendingBuildingsValidation";
import {isPlayerFaction} from "../../../factions/isPlayerFaction";
import {FACTIONS} from "../../../../constants/factions";
import {addMoreElementalsConstructionTokens} from "../../../factions/elementals/addMoreElementalsConstructionTokens";
import {getTokenConfig} from "../../../buildings/getTokenConfig";
import {checkIfTurnWillBeAutoCompleted} from "../../turn/executePhaseAutoActions";

export const CONSTRUCTION_PLAN_APPLY_STATUSES = {
  // План применен (часть строений могла не встать — см. skipped)
  APPLIED: 'applied',
  // Применять нечего или уже поздно
  SKIPPED: 'skipped',
  // Соединение не открыто: план ждет и применится при следующей возможности
  DEFERRED: 'deferred',
};

export const TEXT_PLANNED_BUILDINGS_SKIPPED = 'Не удалось построить запланированное:';

// Ключ хода, в начале которого план ждет применения, или null: наступил
// собственный ход строительства, план не пуст (или выбрано автоматическое
// завершение хода — тогда и пустой план завершает ход) и в этот ход еще не
// применялся
export const getDueConstructionPlanTurnKey = (state) => {
  if (getConstructionPlanContext(state) !== CONSTRUCTION_PLAN_CONTEXTS.OWN_TURN) {
    return null;
  }
  if (selectConstructionPlanEntries(state).length === 0 && !selectConstructionPlanIsAutoCompleteTurn(state)) {
    return null;
  }
  const player = getConstructionPlanPlayer(state);
  const turnKey = getConstructionPlanTurnKey(selectRound(state), player.id);
  return selectConstructionPlanAppliedTurnKey(state) === turnKey ? null : turnKey;
};

const withSelectedToken = (state, token) => ({
  ...state,
  private: {
    ...state.private,
    selectedEntities: {...state.private?.selectedEntities, selectedToken: token},
  },
});

// Жетоны элементов на ход тянет панель строительства в начале хода
// (NewBuildingToolbar). Если панели нет (например, открыта инспекция ячейки),
// тянем здесь — тем же способом и только недостающие, так что порядок с панелью
// не важен
const ensureElementalsConstructionTokens = (store) => {
  const state = store.getState();
  const player = selectCurrentPlayer(state);
  if (!player || !isPlayerFaction(player, FACTIONS.ELEMENTALS)) {
    return;
  }
  const tokens = selectElementalsConstructionTokens(state) || [];
  const additionalCount = player.constructionPhaseActions - tokens.length;
  if (additionalCount <= 0) {
    return;
  }
  const newTokens = addMoreElementalsConstructionTokens(tokens, additionalCount);
  if (newTokens.length > tokens.length) {
    store.dispatch(elementalsSetRandomElements(newTokens));
  }
};

const getBuildingLabel = (token) => getTokenConfig(token)?.label || token;

/**
 * Применить план в начале собственного хода строительства: каждое
 * запланированное строение проходит те же проверки, что и клик по полю
 * (resolveConstructionPlacement по живому состоянию), и ставится теми же
 * действиями строительства, что уходят на сервер. Затем строения хода
 * проверяются и невалидные подсвечиваются — как по кнопке завершения фазы, но
 * без ее завершения.
 *
 * Строения, которые встать уже не могут (ячейку заняли, не хватает монет или
 * действий, нет элемента), пропускаются. Все сообщения — пропущенные строения
 * и ошибки расположения — собираются в один текст (тост в состоянии один).
 *
 * deps:
 *  - isSocketOpen() — открыто ли соединение матча: без него действия не дойдут
 *    до сервера, а LOAD_STATE после переподключения затер бы их;
 *  - checkIfTurnWillBeAutoCompleted(store) — подменяется в тестах.
 *
 * Возвращает {status, placed, skipped, message, turnKey, isAutoCompleteTurn}:
 * isAutoCompleteTurn — выбор автоматического завершения хода. Его приходится
 * забирать отсюда: вместе с планом применение сбрасывает и чекбокс.
 */
export const applyConstructionPlan = (store, deps = {}) => {
  const {
    isSocketOpen = () => true,
    checkIfTurnAutoCompleted = checkIfTurnWillBeAutoCompleted,
  } = deps;

  const state = store.getState();
  const turnKey = getDueConstructionPlanTurnKey(state);
  if (!turnKey) {
    return {status: CONSTRUCTION_PLAN_APPLY_STATUSES.SKIPPED, placed: [], skipped: []};
  }
  if (!isSocketOpen()) {
    return {status: CONSTRUCTION_PLAN_APPLY_STATUSES.DEFERRED, placed: [], skipped: [], turnKey};
  }

  const entries = selectConstructionPlanEntries(state);
  const isAutoCompleteTurn = selectConstructionPlanIsAutoCompleteTurn(state);
  // План забирается ДО отправки действий: собственные постройки не должны
  // сниматься с плана как «чужие», а повторный вызов — строить еще раз
  store.dispatch(markConstructionPlanApplied(turnKey));

  // Сервер сам завершит этот ход (у игрока нет городов) — строить нечего
  if (checkIfTurnAutoCompleted(store)) {
    return {status: CONSTRUCTION_PLAN_APPLY_STATUSES.SKIPPED, placed: [], skipped: [], turnKey};
  }

  ensureElementalsConstructionTokens(store);

  const placed = [];
  const skipped = [];
  entries.forEach((entry) => {
    const liveState = withSelectedToken(store.getState(), entry.token);
    const placement = resolveConstructionPlacement(liveState, entry.hexKey);

    if (placement.kind !== CONSTRUCTION_PLACEMENT_KINDS.COMMON && placement.kind !== CONSTRUCTION_PLACEMENT_KINDS.SPECIAL) {
      skipped.push({entry, reason: placement.errorMessage || TEXT_CELL_NOT_AVAILABLE_FOR_CONSTRUCTION});
      return;
    }

    if (isPlayerFaction(selectCurrentPlayer(liveState), FACTIONS.ELEMENTALS) && placement.elementIndex !== null) {
      store.dispatch(elementalsSpendElement(placement.elementIndex));
    }
    if (placement.kind === CONSTRUCTION_PLACEMENT_KINDS.SPECIAL) {
      store.dispatch(setSpecialBuilding(entry.hexKey, entry.token));
    } else {
      store.dispatch(setHexToken(entry.hexKey, entry.token, placement.priceCoins, false, placement.isFreeAction));
    }
    placed.push({entry, priceCoins: placement.priceCoins});
  });

  const {message: invalidMessage} = placed.length > 0
    ? highlightInvalidPendingBuildings(store, {showToast: false})
    : {message: null};

  const messageParts = [];
  if (skipped.length > 0) {
    messageParts.push([
      TEXT_PLANNED_BUILDINGS_SKIPPED,
      ...skipped.map(({entry, reason}) => `${getBuildingLabel(entry.token)}: ${String(reason).split('\n')[0]}`),
    ].join('\n• '));
  }
  if (invalidMessage) {
    messageParts.push(invalidMessage);
  }

  return {
    status: CONSTRUCTION_PLAN_APPLY_STATUSES.APPLIED,
    placed,
    skipped,
    message: messageParts.length > 0 ? messageParts.join('\n\n') : null,
    turnKey,
    isAutoCompleteTurn,
  };
};
